'use client'

import { useMandatoryContext } from '@repo/hooks'
import {
  createContext,
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider'

export interface ServiceUnavailableContextProps {
  isServiceUnavailableDialogOpen: boolean
  signalServiceUnavailable: () => void
  closeServiceUnavailableDialog: () => void
}

export const ServiceUnavailableContext = createContext<ServiceUnavailableContextProps | null>(null)

export const useServiceUnavailable = () => {
  return useMandatoryContext(ServiceUnavailableContext, 'ServiceUnavailableContext')
}

export type ServiceUnavailableContextProviderProps = PropsWithChildren

/**
 * Provider component that provides the ServiceUnavailableContext to its children.
 * Used to display a service unavailable dialog when the service is unavailable.
 */
const ServiceUnavailableContextProvider = ({
  children
}: ServiceUnavailableContextProviderProps) => {
  const [isServiceUnavailableDialogOpen, setIsServiceUnavailableDialogOpen] = useState(false)
  const [hasServiceUnavailableDialogBeenOpened, setHasServiceUnavailableDialogBeenOpened] =
    useState(false)
  const { manualServiceUnavailable } = useFeatureFlags()

  /**
   * Opens the service unavailable dialog if it has not been opened before.
   * Sets the state to indicate that the dialog has been opened.
   */
  const signalServiceUnavailable = useCallback(() => {
    if (!hasServiceUnavailableDialogBeenOpened) {
      setIsServiceUnavailableDialogOpen(true)
      setHasServiceUnavailableDialogBeenOpened(true)
    }
  }, [hasServiceUnavailableDialogBeenOpened])

  useEffect(() => {
    if (manualServiceUnavailable) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      signalServiceUnavailable()
    }
  }, [signalServiceUnavailable, manualServiceUnavailable])

  const value = useMemo(
    () => ({
      isServiceUnavailableDialogOpen,
      signalServiceUnavailable,
      closeServiceUnavailableDialog: () => setIsServiceUnavailableDialogOpen(false)
    }),
    [signalServiceUnavailable, isServiceUnavailableDialogOpen]
  )

  return (
    <ServiceUnavailableContext.Provider value={value}>
      {children}
    </ServiceUnavailableContext.Provider>
  )
}

export default memo(ServiceUnavailableContextProvider)
